import type { UrlScopingRule } from '@core/abTesting/backend/types'

/**
 * Normalizes a URL to a comparable string: hostname + pathname, no trailing slash, lowercase.
 * Handles rule URLs with or without protocol.
 */
function normalizeUrl(url: string): string {
  try {
    const withProtocol =
      url.startsWith('http://') || url.startsWith('https://')
        ? url
        : `https://${url}`
    const parsed = new URL(withProtocol)
    const pathname = parsed.pathname.replace(/\/$/, '') || ''
    return `${parsed.hostname}${pathname}`.toLowerCase()
  } catch {
    return url.toLowerCase()
  }
}

/**
 * Returns the current page URL normalized for comparison.
 * Uses currentUrl when provided (e.g. for tests or SSR), otherwise window.location.
 */
function getCurrentPageUrlNormalized(currentUrl?: string): string | null {
  if (currentUrl !== undefined) {
    return normalizeUrl(currentUrl)
  }
  if (typeof window === 'undefined' || !window.location) {
    return null
  }
  const { hostname, pathname } = window.location
  const path = pathname.replace(/\/$/, '') || ''
  return `${hostname}${path}`.toLowerCase()
}

/**
 * Tests whether the current page URL satisfies any of the given URL scoping rules.
 * Used at read time to filter experiments per page.
 *
 * - Missing or empty rules → returns true (no scoping = experiment applies everywhere).
 * - equals: normalized current URL must equal normalized rule URL.
 * - starts_with: normalized current URL must start with normalized rule URL.
 * - contains: normalized current URL must include the rule substring (case-insensitive).
 *   Rule URLs are not normalized so bare path fragments like `/products/` work.
 *
 * @param rules - Per-experiment URL scoping rules from the API
 * @param currentUrl - Optional URL to test (for tests or SSR); defaults to window.location
 * @returns true if the page is in scope for these rules, false otherwise
 */
export function currentPageMatchesUrlScoping(
  rules: UrlScopingRule[] | undefined | null,
  currentUrl?: string,
): boolean {
  if (!rules || rules.length === 0) {
    return true
  }

  const normalizedCurrent = getCurrentPageUrlNormalized(currentUrl)
  if (normalizedCurrent === null) {
    return false
  }

  return rules.some((rule) => {
    const normalizedRule = normalizeUrl(rule.url)
    if (rule.match_type === 'equal') {
      return normalizedCurrent === normalizedRule
    }
    if (rule.match_type === 'starts_with') {
      return (
        normalizedCurrent === normalizedRule ||
        normalizedCurrent.startsWith(`${normalizedRule}`)
      )
    }
    if (rule.match_type === 'contains') {
      const containsRule =
        rule.url.startsWith('http://') || rule.url.startsWith('https://')
          ? normalizeUrl(rule.url)
          : rule.url.toLowerCase()
      return normalizedCurrent.includes(containsRule)
    }
    return false
  })
}
